import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';

type CompButtonDrawerLayout = 'horizontal' | 'vertical';
type CompButtonDrawerScale = 'full' | 'half' | 'offset';

interface CompButtonDefaultDrawerProps extends React.HTMLAttributes<HTMLDivElement> {
    children?: React.ReactNode;
    drawerId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    ariaLabelDrawer?: string;
    ariaLabelDrawerButton?: string;
    layout?: CompButtonDrawerLayout;
    scale?: CompButtonDrawerScale;
}

const getFocusableElements = (container: HTMLElement | null) => {
    if (!container) {
        return [] as HTMLElement[];
    }

    return Array.from(
        container.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
    ).filter(element => !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden'));
};

const drawerScaleStyles: Record<CompButtonDrawerLayout, Record<CompButtonDrawerScale, string>> = {
    horizontal: {
        full: 'inset-y-0 right-0 h-full w-full',
        half: 'inset-y-0 right-0 h-full w-[calc(100%-24px)] sm:w-1/2',
        offset: 'inset-y-0 right-0 h-full w-[calc(100%-24px)] sm:w-[calc(100%-64px)]',
    },
    vertical: {
        full: 'bottom-0 left-0 h-full w-full',
        half: 'bottom-0 left-0 h-1/2 w-full',
        offset: 'bottom-0 left-0 h-[calc(100%-24px)] w-full sm:h-[calc(100%-64px)]',
    },
};

const CompButtonDefaultDrawer = ({
    ariaLabelDrawer,
    ariaLabelDrawerButton,
    children,
    drawerId,
    layout = 'horizontal',
    onOpenChange,
    open,
    scale = 'full',
}: CompButtonDefaultDrawerProps) => {
    const drawerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open || typeof document === 'undefined') {
            return;
        }

        const ownerDocument = drawerRef.current?.ownerDocument ?? document;
        const previousActiveElement =
            ownerDocument.activeElement instanceof HTMLElement ? ownerDocument.activeElement : null;
        const originalOverflow = ownerDocument.body.style.overflow;

        ownerDocument.body.style.overflow = 'hidden';

        const focusableElements = getFocusableElements(drawerRef.current);
        (focusableElements[0] ?? drawerRef.current)?.focus();

        const handleKeyDown = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onOpenChange(false);
                return;
            }

            if (event.key !== 'Tab' || !drawerRef.current) {
                return;
            }

            const currentFocusableElements = getFocusableElements(drawerRef.current);

            if (currentFocusableElements.length === 0) {
                event.preventDefault();
                drawerRef.current.focus();
                return;
            }

            const firstFocusableElement = currentFocusableElements[0];
            const lastFocusableElement = currentFocusableElements[currentFocusableElements.length - 1];
            const activeElement = ownerDocument.activeElement;
            const activeElementIsInsideDrawer = drawerRef.current.contains(activeElement);

            if (event.shiftKey) {
                if (activeElement === firstFocusableElement || !activeElementIsInsideDrawer) {
                    event.preventDefault();
                    lastFocusableElement.focus();
                }

                return;
            }

            if (activeElement === lastFocusableElement || !activeElementIsInsideDrawer) {
                event.preventDefault();
                firstFocusableElement.focus();
            }
        };

        ownerDocument.addEventListener('keydown', handleKeyDown);

        return () => {
            ownerDocument.removeEventListener('keydown', handleKeyDown);
            ownerDocument.body.style.overflow = originalOverflow;
            previousActiveElement?.focus();
        };
    }, [onOpenChange, open]);

    if (!open) {
        return null;
    }

    const drawerNode = (
        <>
            <div
                aria-hidden="true"
                className="fixed inset-0 z-40 bg-velux-black opacity-80"
                data-testid="comp-button-default-drawer-overlay"
                onClick={() => onOpenChange(false)}
            />
            <aside
                ref={drawerRef}
                id={drawerId}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabelDrawer}
                data-layout={layout}
                data-scale={scale}
                data-testid="comp-button-default-drawer"
                tabIndex={-1}
                className={cn(
                    'fixed z-50 bg-white outline-none shadow-2xl',
                    layout === 'vertical' ? 'translate-y-0' : 'translate-x-0',
                    drawerScaleStyles[layout][scale]
                )}
            >
                <button
                    type="button"
                    aria-label={ariaLabelDrawerButton ?? 'Close drawer'}
                    className="absolute right-6 top-6 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-velux-ui-grey text-xl leading-none text-velux-black"
                    onClick={() => onOpenChange(false)}
                >
                    <span aria-hidden="true">×</span>
                </button>
                <div className="absolute inset-0 overflow-auto px-6 py-16 sm:px-8 md:px-10 lg:px-9">
                    {children}
                </div>
            </aside>
        </>
    );

    if (typeof document === 'undefined') {
        return drawerNode;
    }

    return createPortal(drawerNode, document.body);
};

export { CompButtonDefaultDrawer };